import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createBankAccount,
  getAllActiveBankAccounts,
  getAllBankAccounts,
  getBankAccountByID,
  updateBankAccount,
} from "./services/bankaccount.services";
import { clearBankAccountDetails } from "./bankaccountSlice";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";
import FormTextArea from "../../components/formFields/FormTextArea";

const CreateBankAccount: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const BankAccountDetail = useAppSelector(
    (state) => state.bankaccount.BankAccountDetail
  );
  const BankAccountID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.bankaccount.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    return () => {
      dispatch(clearBankAccountDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (BankAccountID) {
      dispatch(getBankAccountByID(BankAccountID));
    }
  }, [BankAccountID]);

  useEffect(() => {
    setFormKey((prevKey) => prevKey + 1);
  }, [BankAccountDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!BankAccountID) {
        const insertPayload = {
          BankName: values.BankName,
          AccountNo: values.AccountNo,
          IFSCCode: values.IFSCCode,
          BranchName: values.BranchName,
          Address: values.Address,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createBankAccount(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearBankAccountDetails());
          if (isIndirect) {
            dispatch(getAllActiveBankAccounts());
          } else {
            dispatch(getAllBankAccounts());
          }
        }
      } else {
        const editPayload = {
          ID: BankAccountID,
          BankName: values.BankName,
          AccountNo: values.AccountNo,
          IFSCCode: values.IFSCCode,
          BranchName: values.BranchName,
          Address: values.Address,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateBankAccount(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearBankAccountDetails());
          dispatch(getAllBankAccounts());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={BankAccountDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem className="master-drawer-header">
                <Typography.h4 className="m-0">
                  {BankAccountID ? "Update Bank Account" : "Add Bank Account"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                >
                  <RxCross1 />
                </IconButton>
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="BankName"
                  label="Bank Name"
                  placeholder="Bank Name"
                  component={FormTextField}
                  astrike={true}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="AccountNo"
                  label="Account No"
                  placeholder="Account No"
                  component={FormTextField}
                  astrike={true}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="IFSCCode"
                  label="IFSC Code"
                  placeholder="IFSC Code"
                  component={FormTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="BranchName"
                  label="Branch"
                  placeholder="Branch"
                  component={FormTextField}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="Address"
                  label="Address"
                  placeholder="Address"
                  component={FormTextArea}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  placeholder="i.e. Active"
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={BankAccountID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />
                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateBankAccount;
